import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Container,
  Card,
  Stack,
  Alert,
  Row,
  Col,
  Form,
} from "react-bootstrap";
import CompanyService from "../../services/company.service";
import { Company } from "../../models/Company";
import { ModalCompanyForm } from "./ModalCompanyForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus } from "@fortawesome/free-solid-svg-icons";
import DeleteButton from "../../Helpers/deleteButton";

type Notification = {
  show: boolean;
  message: string;
  title: string;
  variant: "success" | "danger";
};

export const Companies: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [modalShow, setModalShow] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [filterText, setFilterText] = useState("");

  const loadCompanies = async () => {
    try {
      const data = await CompanyService.getCompanies();
      setCompanies(data);
    } catch {
      setNotification({
        show: true,
        title: "Error",
        message: "Error al cargar compañías",
        variant: "danger",
      });
    }
  };

  const getFilteredCompanies = () => {
    const search = filterText.toLowerCase();

    return companies.filter((c) => {
      return (
        c.name.toLowerCase().includes(search) ||
        c.id.toString().includes(search)
      );
    });
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const handleSave = async (company: Company) => {
    try {
      if (company.id) {
        const response = await CompanyService.updateCompany(company);

        if (response.ok) {
          setNotification({
            show: true,
            title: "Éxito",
            message: "Compañía actualizada correctamente",
            variant: "success",
          });
        } else {
          const errorMessage = await response.text();
          throw new Error(errorMessage || "Error al actualizar la compañía");
        }
      } else {
        const response = await CompanyService.addCompany(company);

        if (response.ok) {
          setNotification({
            show: true,
            title: "Éxito",
            message: "Compañía agregada correctamente",
            variant: "success",
          });
        }
      }
      setModalShow(false);
      setSelectedCompany(null);
      loadCompanies();
    } catch (error) {
      console.error("Error:", error);
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: error instanceof Error ? error.message : "",
      });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await CompanyService.deleteCompany(id);
      if (response.ok) {
        setNotification({
          show: true,
          variant: "success",
          title: "Éxito",
          message: response.message || "Compañía eliminada correctamente",
        });
      } else {
        const errorMessage = await response.text();
        throw new Error(errorMessage || "Error al eliminar la compañía");
      }
      await loadCompanies();
    } catch {
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: "Error al eliminar la compañía",
      });
    }
  };

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <>
      <div className="notification-container">
        {notification && (
          <Alert
            variant={notification.variant}
            onClose={() => setNotification(null)}
            className="notification-alert"
            dismissible
          >
            <Alert.Heading>{notification.title}</Alert.Heading>
            {notification.message}
          </Alert>
        )}
      </div>

      <Container className="pt-3">
        <Card>
          <Card.Body>
            <Card.Title>Compañías</Card.Title>
            <hr />
            <Row className="mb-3 g-3 align-items-center">
              <Col md={5}>
                <Form.Control
                  type="text"
                  placeholder="Buscar..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </Col>
              <Col
                md={7}
                className="d-flex justify-content-md-end align-items-center"
              >
                <Stack direction="horizontal" gap={2}>
                  <Button
                    variant="primary"
                    className="d-flex  align-items-center"
                    onClick={() => {
                      setSelectedCompany(null);
                      setModalShow(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} className="white" />
                    <span className="ms-2">Nueva compañía</span>
                  </Button>
                </Stack>
              </Col>
            </Row>

            <Table striped bordered hover>
              <thead>
                <tr className="text-center">
                  <th>Id</th>
                  <th>Nombre</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredCompanies().map((c) => (
                  <tr key={c.id}>
                    <td>{c.id}</td>
                    <td>{c.name}</td>
                    <td className="text-center">
                      <Stack
                        direction="horizontal"
                        gap={2}
                        className="justify-content-center"
                      >
                        <Button
                          variant="primary"
                          size="sm"
                          title="Editar"
                          onClick={() => {
                            setSelectedCompany(c);
                            setModalShow(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} className="white" />
                        </Button>
                        <DeleteButton onClick={() => handleDelete(c.id)} />
                      </Stack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
        <ModalCompanyForm
          show={modalShow}
          onHide={() => setModalShow(false)}
          onSave={handleSave}
          company={selectedCompany}
        />
      </Container>
    </>
  );
};
